import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Zap } from "lucide-react";
import { track } from "../utils/analytics";

const STICKER_TAGLINE = "Tu n'as pas écouté…";
const STICKER_TAGLINE2 = "Bienvenue dans l'arène.";
const STICKER_SUB = "Sticker scanné. Round 1 — tu es au bon endroit.";

const easeOut = [0.2, 0.9, 0.2, 1];

function useStickerParam() {
  return useMemo(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const src = params.get("source") || params.get("src") || params.get("utm_source");
    return src && src.toLowerCase() === "sticker" ? src.toLowerCase() : null;
  }, []);
}

// Word-by-word reveal — each word is a motion.span for a cinematic entry.
function AnimatedTagline({ text, delay = 0, glitchClass = "" }) {
  const words = text.split(/(\s+)/);
  return (
    <span className={`inline-block ${glitchClass}`}>
      {words.map((w, i) => (
        <motion.span
          key={i}
          initial={{ opacity: 0, y: 30, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{
            duration: 0.55,
            delay: delay + i * 0.045,
            ease: easeOut,
          }}
          className="inline-block whitespace-pre"
        >
          {w}
        </motion.span>
      ))}
    </span>
  );
}

export default function Hero({ t }) {
  const stickerSource = useStickerParam();
  const isSticker = !!stickerSource;
  const [ribbonOpen, setRibbonOpen] = useState(isSticker);

  const tagline = isSticker ? STICKER_TAGLINE : t.hero.tagline;
  const tagline2 = isSticker ? STICKER_TAGLINE2 : t.hero.tagline2;
  const sub = isSticker ? STICKER_SUB : t.hero.sub;

  // Fire GA4 event once when sticker traffic lands.
  useEffect(() => {
    if (!isSticker) return;
    track("sticker_landing", { source: stickerSource });
    const timer = setTimeout(() => setRibbonOpen(false), 6500);
    return () => clearTimeout(timer);
  }, [isSticker, stickerSource]);

  const glitchClass = isSticker ? "hero-glitch-strong" : "hero-glitch";

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[640px] overflow-hidden grain vignette"
    >
      {/* Sticker landing ribbon */}
      {ribbonOpen && (
        <div
          data-testid="sticker-ribbon"
          className="sticker-ribbon absolute top-0 left-0 right-0 z-40 bg-leaf text-ink"
        >
          <div className="flex items-center justify-center gap-3 px-6 py-2.5 md:py-3">
            <Zap size={14} strokeWidth={2} />
            <p className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase font-medium">
              Sticker scanné · Bienvenue dans l&apos;arène
            </p>
          </div>
        </div>
      )}

      {/* Background image */}
      <div className="absolute inset-0">
        <img
          srcSet="/img/hero-800.webp 800w, /img/hero-1200.webp 1200w, /img/hero.webp 1920w"
          sizes="100vw"
          src="/img/hero-1200.webp"
          alt="Combat sports — Nono"
          fetchPriority="high"
          decoding="async"
          className="w-full h-full object-cover object-center animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 via-transparent to-transparent" />
      </div>

      {/* Big typographic overlay */}
      <div className="absolute inset-x-0 top-[58%] -translate-y-1/2 z-[3] pointer-events-none select-none">
        <p className="font-display text-[28vw] md:text-[22vw] leading-none tracking-tighter text-bone/[0.045] text-center">
          COMBAT
        </p>
      </div>

      {/* Content */}
      <div className="absolute inset-0 z-10 flex flex-col justify-between px-6 md:px-12 lg:px-20 pt-28 md:pt-36 pb-10 md:pb-16">
        <div className="flex items-center gap-4">
          <span className="block h-px w-10 bg-leaf"></span>
          <p data-testid="hero-eyebrow" className="text-[11px] tracking-[0.4em] uppercase text-bone/80">
            {t.hero.eyebrow}
          </p>
        </div>

        <div className="max-w-5xl">
          <h1
            data-testid="hero-tagline"
            className="font-serif font-light text-bone leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[6.5rem]"
          >
            <span className="block">
              <AnimatedTagline text={tagline} delay={0} glitchClass={glitchClass} />
            </span>
            <span className="block italic text-bone/90">
              <AnimatedTagline text={tagline2} delay={0.25} glitchClass={glitchClass} />
            </span>
          </h1>

          <motion.p
            data-testid="hero-sub"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.75, ease: easeOut }}
            className="mt-8 max-w-md text-ash text-sm md:text-base leading-relaxed"
          >
            {sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.95, ease: easeOut }}
            className="mt-10 flex items-center gap-6"
          >
            <button
              data-testid="hero-cta"
              onClick={() => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 border border-bone/40 hover:border-leaf hover:bg-leaf/10 transition-all duration-500 px-7 py-4"
            >
              <span className="text-[11px] tracking-[0.32em] uppercase text-bone">{t.hero.cta}</span>
              <ArrowRight size={16} strokeWidth={1.5} className="text-bone group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.div>
        </div>

        <div className="flex items-end justify-between">
          <button
            data-testid="hero-scroll"
            onClick={() => document.getElementById("work")?.scrollIntoView({ behavior: "smooth" })}
            className="hidden md:flex items-center gap-2 text-bone/60 hover:text-bone transition-colors"
          >
            <ChevronDown size={14} className="animate-bounce" />
            <span className="text-[10px] tracking-[0.4em] uppercase">{t.hero.scroll}</span>
          </button>
          <div className="ml-auto text-right">
            <p translate="no" className="notranslate text-[10px] tracking-[0.4em] uppercase text-bone/60">Alexis Lecoeuvre</p>
            <p className="text-[10px] tracking-[0.4em] uppercase text-bone/40 mt-1">© 2026</p>
          </div>
        </div>
      </div>
    </section>
  );
}
